import { useEffect, useRef, useState } from "react";
import { TURNSTILE_SITE_KEY } from "../../config.js";

// Cloudflare Turnstile client widget. Renders nothing when
// VITE_TURNSTILE_SITE_KEY is unset (dev + staging), which pairs with the
// server's fail-open behaviour when TURNSTILE_SECRET_KEY is unset. Calls
// `onToken` with the captured response token whenever the widget solves
// (and again with null if the token expires, so the form re-requests).

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        opts: {
          sitekey: string;
          action?: string;
          callback?: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "flexible" | "compact";
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
    };
  }
}

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

let scriptPromise: Promise<void> | null = null;

function loadTurnstile(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src^="${SCRIPT_SRC.split("?")[0]}"]`,
    );
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("turnstile load failed")));
      return;
    }
    const s = document.createElement("script");
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("turnstile load failed"));
    document.head.appendChild(s);
  });
  return scriptPromise;
}

export function Turnstile({
  action,
  onToken,
  resetSignal,
}: {
  action: string;
  onToken: (token: string | null) => void;
  // Turnstile tokens are SINGLE-USE: once a token is sent to the server it's
  // consumed, even on a 4xx/429. After ANY non-2xx the caller must bump this
  // counter so the widget re-challenges and hands back a FRESH token — without
  // it the next attempt resends the now-spent token and is hard-blocked
  // (the common "mistyped DOB → retry" path). 0/undefined means no reset.
  resetSignal?: number;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || !ref.current) return;
    let cancelled = false;
    loadTurnstile()
      .then(() => {
        if (cancelled || !ref.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(ref.current, {
          sitekey: TURNSTILE_SITE_KEY,
          action,
          theme: "auto",
          // `flexible` lets the widget take the full width of its
          // container, matching the rest of the login form inputs.
          size: "flexible",
          callback: (token) => {
            setError(null);
            onToken(token);
          },
          "expired-callback": () => {
            // Token lapsed before submit. Reset the widget so it
            // re-challenges and tell the candidate they can retry.
            onToken(null);
            setError("Bot-check expired. Solve it again to continue.");
            if (widgetIdRef.current && window.turnstile) {
              try {
                window.turnstile.reset(widgetIdRef.current);
              } catch {
                /* ignore */
              }
            }
          },
          "error-callback": () => {
            // Challenge errored. Reset to give a fresh widget; surface an
            // alert so the candidate can retry. onToken(null) clears the held
            // token so the gated submit stays disabled until a fresh challenge
            // is solved (the production endpoint rejects a tokenless request).
            onToken(null);
            setError("Bot-check failed. Refresh the page or try again.");
            if (widgetIdRef.current && window.turnstile) {
              try {
                window.turnstile.reset(widgetIdRef.current);
              } catch {
                /* ignore */
              }
            }
          },
        });
      })
      .catch(() => {
        setError(
          "Verification couldn't load. Check your connection, refresh, or ask your invigilator.",
        );
        // Script blocked or failed to load. In dev/staging (no secret) the
        // server fail-opens; in production the gated submit stays disabled
        // because no token can be produced, so onToken(null) keeps the form
        // from POSTing a request the server would reject.
        onToken(null);
      });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
      }
      widgetIdRef.current = null;
    };
  }, [action, onToken]);

  // Imperative reset on demand (caller bumps resetSignal after a failed POST).
  // Re-challenge the widget for a fresh single-use token and clear the held one
  // so the gated submit re-disables until the new challenge solves. Skip the
  // initial render (resetSignal 0/undefined) so we don't reset a just-minted
  // token. The callback's onToken(null) → callback(newToken) flow re-enables
  // submit once the fresh token lands.
  useEffect(() => {
    if (!resetSignal) return;
    onToken(null);
    if (widgetIdRef.current && window.turnstile) {
      try {
        window.turnstile.reset(widgetIdRef.current);
      } catch {
        /* widget already gone — the next render path re-renders it */
      }
    }
    // Intentionally NOT depending on onToken: we only want to fire on a new
    // reset signal, and onToken is stable (useCallback) in the caller.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal]);

  if (!TURNSTILE_SITE_KEY) return null;
  return (
    <div className="w-full space-y-1">
      <div ref={ref} data-testid="turnstile" className="w-full" />
      {error && (
        // These are hard failures (expired / errored / script blocked) that
        // BLOCK exam entry — surface them as destructive, not low-contrast
        // muted text, so they're legible and read as the blocker they are.
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}
